import type { CharacterRuntimeState } from '../character/runtime-state';
import { getAttackPhase, isAttackInvulnerable } from '../combat/attack';
import type { AttackDefinition, ProjectedAttackHitbox } from '../combat/attack';
import type { CombatResult, PendingHit } from '../combat/resolution';
import type { BattleEvent } from './battle-event';
import type { DebugSnapshot } from './debug-snapshot';

// Read-only projections. Never used to make Gameplay decisions.
export const projectCombatCharacter = (
  character: Readonly<CharacterRuntimeState>, definitions: ReadonlyMap<string, AttackDefinition>,
  player: number, logicalFrame: number,
) => {
  const instance = character.currentAttack;
  const definition = instance === null ? undefined : definitions.get(instance.attackId);
  const attack = definition !== undefined && instance !== null
    && isAttackInvulnerable(definition, instance.elapsedFrames);
  const reasons = { airborne: character.isAirborneInvincible,
    postWake: character.postWakeInvincibilityFrames > 0, attack, ko: character.state === 'KO' };
  const finalInvincible = Object.values(reasons).some(Boolean);
  const warnings: string[] = [];
  if (character.state !== 'Hitstun' && reasons.airborne) warnings.push('A: airborne outside Hitstun');
  if (character.isGrounded && reasons.airborne) warnings.push('B: airborne while grounded');
  if (character.state === 'Idle' && finalInvincible) warnings.push('C: Idle and invincible');
  if (instance === null && attack) warnings.push('D: attack invincibility without attack');
  if (character.postWakeInvincibilityFrames === 0 && reasons.postWake) warnings.push('E: postWake with zero frames');
  return {
    player: `P${player + 1}`, characterId: character.characterDefinitionId, runtimeId: character.id,
    logicalFrame, state: character.state, stateFrame: character.stateFrame,
    lastTransitionReason: character.lastTransitionReason, isGrounded: character.isGrounded,
    isAirborneInvincible: reasons.airborne, postWakeInvincibilityFrames: character.postWakeInvincibilityFrames,
    attackInvincible: attack, stateIsKO: reasons.ko, finalInvincible, reasons, warnings,
    currentAttackId: instance?.attackId ?? null, attackInstanceId: instance?.attackInstanceId ?? null,
    attackElapsedFrames: instance?.elapsedFrames ?? null,
    attackPhase: definition !== undefined && instance !== null ? getAttackPhase(definition, instance.elapsedFrames) : null,
    attackInvulnerableFrames: definition?.invulnerability.map((range) => ({ ...range })) ?? [],
    withinAttackInvulnerableFrames: attack, startFacing: instance?.startFacing ?? null,
    stunFramesRemaining: character.stunFramesRemaining,
    knockdownFramesRemaining: character.knockdownFramesRemaining, wakeFramesRemaining: character.wakeFramesRemaining,
    landingRecoveryFramesRemaining: character.landingRecoveryFramesRemaining,
    position: { ...character.position }, velocity: { ...character.velocity },
    pendingKnockback: character.pendingKnockback === null ? null : { ...character.pendingKnockback },
    hp: character.hp, gauge: character.gauge,
    counterWindowActive: [...character.combatEffects.values()].some((effect) => effect.type === 'COUNTER_WINDOW' && effect.remainingFrames > 0),
    hitRecord: instance === null ? [] : [...instance.hitRecord],
  };
};

export type CombatCharacterDiagnostic = ReturnType<typeof projectCombatCharacter>;
export interface CombatDiagnosticResult {
  logicalFrame: number; attacker: string; defender: string; attackId: string; hitId: string;
  attackInstanceId: string; source: string; result: CombatResult['result'];
  reasons: CombatCharacterDiagnostic['reasons']; defenseAtClassification: CombatCharacterDiagnostic;
  hpBefore: number | null; hpAfter: number | null;
}

export class CombatDiagnostics {
  private readonly contacts: { logicalFrame: number; projectedHitboxes: readonly ProjectedAttackHitbox[];
    pendingHits: readonly PendingHit[]; collisionGeometry: DebugSnapshot; players: CombatCharacterDiagnostic[] }[] = [];
  private readonly results: CombatDiagnosticResult[] = [];
  private readonly events: BattleEvent[] = [];
  public contact(check: (typeof this.contacts)[number]): void {
    this.contacts.push(check);
    if (this.contacts.length > 50) this.contacts.shift();
  }
  public record(result: CombatResult, defense: CombatCharacterDiagnostic): void {
    this.results.push({ logicalFrame: result.logicalFrame, attacker: `P${result.attacker + 1}`,
      defender: `P${result.defender + 1}`, attackId: result.attackId, hitId: result.hitId,
      attackInstanceId: result.attackInstanceId, source: result.source, result: result.result,
      reasons: { ...defense.reasons }, defenseAtClassification: defense, hpBefore: null, hpAfter: null });
    if (this.results.length > 50) this.results.shift();
  }
  public application(result: CombatResult, hpBefore: number, hpAfter: number): void {
    const row = this.results.findLast((entry) => entry.logicalFrame === result.logicalFrame
      && entry.attackInstanceId === result.attackInstanceId && entry.hitId === result.hitId
      && entry.defender === `P${result.defender + 1}`);
    if (row !== undefined) { row.hpBefore = hpBefore; row.hpAfter = hpAfter; }
  }
  public event(event: BattleEvent): void {
    this.events.push(event);
    if (this.events.length > 50) this.events.shift();
  }
  public history() {
    return { recentCombatResults: this.results.map((row) => ({ ...row })), recentBattleEvents: [...this.events],
      recentContactChecks: [...this.contacts] };
  }
}
